import classNames from "classnames";
import { FC, useEffect } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { LoadingSpinner, Select } from "@rothoblaas/design-system";
import { applyCascade } from "../notices/applyCascade";
import { NoticeFormField as FormField } from "../notices/NoticeFormField";
import { useNotices } from "../notices/NoticesContext";
import useI18n from "../../../../../hooks/useTranslations";
import { FINISH_EVO, type LoadDurationClassLight } from "../../../../../http/lock_calculator/types";
import { useLockCalculatorCatalog } from "../../hooks/useLockCalculatorCatalog";
import { LC_IMG } from "../../constants";
import styles from "../../LockCalculator.module.css";
import { effectiveGammaMAluminium, type CalculationCoefficients, type LockCalculatorFormValues } from "../../model/form";

/** Service class whose automatic generation is EVO-only (backend: evoRequiredServiceClass). */
const EVO_REQUIRED_SERVICE_CLASS = 3;

// ─── Props ───

interface EnvironmentFormProps {
  readOnly?: boolean;
  onOpenHelp?: (text: string, image?: string) => void;
  /** Values in use, to prefill the editable coefficients when the "custom" switch goes on. */
  coefficients?: CalculationCoefficients;
}

// ─── Component ───

export const EnvironmentForm: FC<EnvironmentFormProps> = ({
  readOnly = false,
  onOpenHelp,
  coefficients,
}) => {
  const i18n = useI18n();
  const form = useFormContext<LockCalculatorFormValues>();
  const {
    control,
    setValue,
    formState: { errors },
  } = form;
  const { reportCascade } = useNotices();
  const continuousControl = useWatch({ control, name: "environment.continuousControl" });
  const standardId = useWatch({ control, name: "environment.standardId" });
  const customCoefficients = useWatch({ control, name: "environment.customCoefficients" });
  const {
    standards,
    serviceClasses,
    loadDurationClasses,
    isLoadingStandards,
    isLoadingServiceClasses,
    isLoadingLoadDurationClasses,
    standardsError,
    serviceClassesError,
    loadDurationClassesError,
  } = useLockCalculatorCatalog();

  // EC5 (Eurocode 5) uses a fixed gammaM regardless of continuous
  // manufacturing control — only NTC2018 differentiates by it, so the
  // toggle below is hidden and cleared when EC5 is selected.
  const isEC5 = standards.find((s) => s.id === standardId)?.code === "EC5";

  useEffect(() => {
    if (isEC5 && continuousControl !== null) {
      applyCascade(form, reportCascade, {
        code: "continuousControlCleared",
        causes: ["environment.standardId"],
        effect: "environment.continuousControl",
        next: null,
        options: { shouldDirty: true, shouldValidate: true },
      });
    }
  }, [isEC5, continuousControl, form, reportCascade]);

  const t = i18n.translation;

  // Switching on starts from the values in use (so nothing jumps), switching off
  // drops every override and the calculation goes back to the normative values.
  const toggleCustomCoefficients = () => {
    const next = !customCoefficients;
    const options = { shouldDirty: true, shouldValidate: true };
    setValue("environment.customCoefficients", next, options);
    setValue("environment.kmodOverride", next ? coefficients?.kmod ?? null : null, options);
    setValue("environment.gammaMConnectionOverride", next ? coefficients?.gammaM ?? null : null, options);
    setValue(
      "environment.gammaMAluminiumOverride",
      next ? (coefficients ? effectiveGammaMAluminium(coefficients) : null) : null,
      options,
    );
  };

  return (
    <div className={styles.compactFormStack}>
      {/* ── STANDARD ── */}
      <Controller
        name="environment.standardId"
        control={control}
        render={({ field }) => (
          <FormField name="environment.standardId"
            label={t.lockCalculatorModule?.env?.standard ?? t.lockCalculatorModule?.connection?.standard}
            error={errors.environment?.standardId}
            showHelp={{
              tooltip: t.lockCalculatorModule?.helpTexts?.standard,
              onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.standard),
            }}
          >
            <div className={styles.imageSelectGrid}>
              {isLoadingStandards && !standardsError && <LoadingSpinner />}
              {standardsError && <span>{i18n.translation.common.errorLoadingData}</span>}
              {standards.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  title={option.description}
                  className={classNames(styles.imageSelectOption, {
                    [styles.imageSelectOptionSelected]: field.value === option.id,
                  })}
                  aria-pressed={field.value === option.id}
                  disabled={readOnly}
                  onClick={() => field.onChange(field.value === option.id ? null : option.id)}
                >
                  {option.code === "EC5" ? (
                    <img
                      className={styles.imageSelectImage}
                      src={`${LC_IMG}/eurocode5.png`}
                      alt={option.description}
                    />
                  ) : option.code === "NTC2018" ? (
                    <img
                      className={styles.imageSelectImage}
                      src={`${LC_IMG}/ntc2018.png`}
                      alt={option.description}
                    />
                  ) : (
                    <span className={styles.imageSelectBadge}>{option.code}</span>
                  )}
                  <span className={styles.imageSelectLabel}>{option.description}</span>
                </button>
              ))}
              {/* Independent of the standard: it only turns the coefficients into editable user values. */}
              <button
                type="button"
                title={t.lockCalculatorModule?.env?.customCoefficientsHint}
                className={classNames(styles.imageSelectOption, {
                  [styles.imageSelectOptionSelected]: customCoefficients,
                })}
                aria-pressed={customCoefficients}
                disabled={readOnly}
                onClick={toggleCustomCoefficients}
              >
                <span className={styles.imageSelectBadge}>✎</span>
                <span className={styles.imageSelectLabel}>{t.lockCalculatorModule?.env?.customCoefficients}</span>
              </button>
            </div>
          </FormField>
        )}
      />

      {/* ── SERVICE CLASS ── */}
      <Controller
        name="environment.serviceClassId"
        control={control}
        render={({ field }) => (
          <FormField name="environment.serviceClassId"
            label={t.lockCalculatorModule?.env?.serviceClass ?? t.lockCalculatorModule?.connection?.serviceClass}
            error={errors.environment?.serviceClassId}
            showHelp={{
              tooltip: t.lockCalculatorModule?.helpTexts?.serviceClass,
              onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.serviceClass),
            }}
          >
            <div className={styles.imageSelectGrid}>
              {isLoadingServiceClasses && !serviceClassesError && <LoadingSpinner />}
              {serviceClassesError && <span>{i18n.translation.common.errorLoadingData}</span>}
              {serviceClasses.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  title={option.description}
                  className={classNames(styles.imageSelectOption, {
                    [styles.imageSelectOptionSelected]: field.value === option.id,
                  })}
                  aria-pressed={field.value === option.id}
                  disabled={readOnly}
                  onClick={() => {
                    const isDeselecting = field.value === option.id;
                    field.onChange(isDeselecting ? null : option.id);
                    // Class 3 makes the EVO configuration mandatory in the
                    // automatic generation (backend: requiresEvo), so the
                    // connectors step starts on EVO. It is only a default:
                    // the user can still switch back to ALUMINIUM there, and
                    // useConnectorSelection reverts it if the chosen lock
                    // has no EVO variant.
                    if (!isDeselecting && option.classNumber === EVO_REQUIRED_SERVICE_CLASS) {
                      applyCascade(form, reportCascade, {
                        code: "finishSetByServiceClass",
                        causes: ["environment.serviceClassId"],
                        effect: "connectorPairs.0.finish",
                        next: FINISH_EVO,
                        options: { shouldDirty: true },
                      });
                      applyCascade(form, reportCascade, {
                        code: "finishSetByServiceClass",
                        causes: ["environment.serviceClassId"],
                        effect: "connectorPairs.1.finish",
                        next: FINISH_EVO,
                        options: { shouldDirty: true },
                        silent: true,
                      });
                    }
                  }}
                >
                  {[1, 2, 3].includes(option.classNumber) ? (
                    <img
                      className={styles.imageSelectImage}
                      src={`${LC_IMG}/service-class-${option.classNumber}.png`}
                      alt={option.description}
                    />
                  ) : (
                    <span className={styles.imageSelectBadge}>{option.classNumber}</span>
                  )}
                  <span className={styles.imageSelectLabel}>{option.description}</span>
                </button>
              ))}
            </div>
          </FormField>
        )}
      />

      {/* ── LOAD DURATION CLASS ── */}
      <Controller
        name="environment.loadDurationClassId"
        control={control}
        render={({ field }) => (
          <FormField name="environment.loadDurationClassId"
            label={
              t.lockCalculatorModule?.env?.loadDurationClass ?? t.lockCalculatorModule?.connection?.loadDurationClass
            }
            error={errors.environment?.loadDurationClassId}
            showHelp={{
              tooltip: t.lockCalculatorModule?.helpTexts?.loadDurationClass,
              onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.loadDurationClass),
            }}
          >
            {({ labelId, isInvalid }) =>
              loadDurationClassesError ? (
                <span>{i18n.translation.common.errorLoadingData}</span>
              ) : (
                <Select<LoadDurationClassLight, false>
                  inputId={labelId}
                  value={loadDurationClasses.find((s) => s.id === field.value) ?? null}
                  options={loadDurationClasses}
                  getOptionValue={(opt) => opt.id}
                  getOptionLabel={(opt) => opt.description}
                  placeholder={t.lockCalculatorModule.selectPlaceholder}
                  isLoading={isLoadingLoadDurationClasses}
                  isDisabled={readOnly}
                  isClearable
                  isInvalid={isInvalid}
                  onChange={(selected) => field.onChange(selected?.id ?? null)}
                />
              )
            }
          </FormField>
        )}
      />

      {/* ── CONTINUOUS CONTROL (COV < 15%) — hidden until a standard is
          chosen (nothing to be applicable/not-applicable to yet), and not
          applicable to EC5 specifically once one is ── */}
      {!!standardId && !isEC5 && (
      <FormField name="environment.continuousControl"
        label={t.lockCalculatorModule?.env?.continuousCtrl}
        showHelp={{
          tooltip: t.lockCalculatorModule?.helpTexts?.continuousCtrl,
          onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.continuousCtrl),
        }}
      >
        <div className={`${styles.imageSelectGrid} ${styles.imageSelectGrid2}`}>
          <button
            type="button"
            className={classNames(styles.imageSelectOption, {
              [styles.imageSelectOptionSelected]: continuousControl === true,
            })}
            aria-pressed={continuousControl === true}
            disabled={readOnly}
            title={t.lockCalculatorModule?.env?.continuousCtrlLabel}
            onClick={() => setValue("environment.continuousControl", true, { shouldDirty: true, shouldValidate: true })}
          >
            <span className={styles.imageSelectBadge}>COV</span>
            <span className={styles.imageSelectLabel}>
              {continuousControl === true
                ? t.lockCalculatorModule.continuousCtrl.active
                : t.lockCalculatorModule.continuousCtrl.inactive}
            </span>
          </button>
          <button
            type="button"
            className={classNames(styles.imageSelectOption, {
              [styles.imageSelectOptionSelected]: continuousControl === false,
            })}
            aria-pressed={continuousControl === false}
            disabled={readOnly}
            title={t.lockCalculatorModule?.env?.continuousCtrlLabel}
            onClick={() => setValue("environment.continuousControl", false, { shouldDirty: true, shouldValidate: true })}
          >
            <span className={styles.imageSelectBadge}>STD</span>
            <span className={styles.imageSelectLabel}>{t.lockCalculatorModule.continuousCtrl.standard}</span>
          </button>
        </div>
      </FormField>
      )}
    </div>
  );
};
